import { context, trace, Span, SpanOptions } from '@dealroadshow/jsbro/OTel/api';
import Container from '@framework/di/Container';
import { IUseOTelTraceContext, OTelTraceContextSymbol } from './react/OTelTraceContext';
import PageTrace from './PageTrace';

const getPageSpan = (container: Container, pageTrace: PageTrace): Span | undefined => {
  if (pageTrace.isStarted()) {
    return pageTrace.getSpan();
  }

  try {
    return container.get<IUseOTelTraceContext>(OTelTraceContextSymbol).span;
  } catch (e) {
    return undefined;
  }
};

const OTelInstrument = <MethodCallback extends (...args: any) => Promise<any>>(
  object: any,
  methodName: string,
  container: Container,
  nameCallback?: (...args: Parameters<MethodCallback>) => string,
  optionsCallback: (...args: Parameters<MethodCallback>) => object = () => ({}),
) => {
  const originalCall = object[methodName];
  const pageTrace = container.get(PageTrace);
  object[methodName] = (...args: any) => {
    const activeSpan = trace.getSpan(context.active());
    const currentSpan = activeSpan ?? getPageSpan(container, pageTrace);
    const ctx = currentSpan ? trace.setSpan(context.active(), currentSpan) : context.active();

    const options: SpanOptions = optionsCallback(...args);
    const pageLink = activeSpan ? undefined : pageTrace.getLink();
    if (pageLink) {
      options.links = [...(options.links ?? []), pageLink];
    }

    const objectName = object.constructor.name;

    return trace.getTracer().startActiveSpan(
      nameCallback ? nameCallback(...args) : `${ objectName }.${ methodName }`,
      options,
      ctx,
      async (span) => {
        try {
          return await originalCall.bind(object)(...args);
        } catch (e) {
          span.recordException({ message: e instanceof Error ? e.message : String(e) });
          throw e;
        } finally {
          span.end();
        }
      },
    );
  };
};

export default OTelInstrument;
